import { useEffect, useState } from "react";
import { fetchWithAuth } from "../../../utils/fetchWithAuth";
import { API_BASE } from "../../../config";
import {
    PieChart,
    Pie,
    Cell,
    Label,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    CartesianGrid,
} from "recharts";

const PURPLE = "#6F3AFF";
const DIM = "rgba(111,58,255,0.18)";
const TICK = { fill: "#8A87A8", fontSize: 11 };
const COLOR_OPEN = "#FFB347";   // orange  – open cases
const COLOR_CLOSED = "#52ea91";   // green   – closed cases

function formatInt(n) {
    return Number(n ?? 0).toLocaleString("de-DE");
}

function formatTypeName(raw) {
    return String(raw ?? "")
        .split("_")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join(" ");
}

function DonutTooltip({ active, payload }) {
    if (!active || !payload?.length) return null;
    const p = payload[0];
    return (
        <div
            style={{
                background: "#180f2e",
                border: "1px solid rgba(111,58,255,0.5)",
                borderRadius: 8,
                padding: "8px 12px",
                fontSize: 12,
                color: "#EDEBFF",
            }}
        >
            <span style={{ fontWeight: 600 }}>{p.name}</span>: {formatInt(p.value)}
        </div>
    );
}

function StepTooltip({ active, payload, label }) {
    if (!active || !payload?.length) return null;
    const total = payload.reduce((s, p) => s + (p.value ?? 0), 0);
    return (
        <div
            style={{
                background: "#180f2e",
                border: "1px solid rgba(111,58,255,0.5)",
                borderRadius: 8,
                padding: "8px 12px",
                fontSize: 12,
                color: "#EDEBFF",
            }}
        >
            <div style={{ fontWeight: 700, marginBottom: 6 }}>{label}</div>
            {payload.map((p) => (
                <div key={p.dataKey} style={{ color: p.fill, marginBottom: 2 }}>
                    {p.name}: {formatInt(p.value)}
                </div>
            ))}
            <div style={{ borderTop: "1px solid rgba(255,255,255,0.1)", marginTop: 6, paddingTop: 4 }}>
                Total: {formatInt(total)}
            </div>
        </div>
    );
}

/**
 * props:
 *   remediation_type        – string  e.g. "ORG_CODE_INVALID"
 *   total_cases             – number
 *   percentage_of_all_cases – number  e.g. 99.67
 *   steps                   – array   [{ remediation_step, count }]
 *   grandTotal              – number  (sum across all types)
 */
function RemediationTypeCard({
    remediation_type,
    total_cases,
    percentage_of_all_cases,
    steps = [],
    grandTotal,
}) {
    const typeName = formatTypeName(remediation_type);
    const pct = Number(percentage_of_all_cases ?? 0);
    const typeCount = Number(total_cases ?? 0);
    const other = Math.max(0, (grandTotal ?? typeCount) - typeCount);

    const donutData = [
        { name: typeName, value: typeCount },
        { name: "Other", value: other },
    ];

    const stepRows = steps.map((s) => ({
        step: `Step ${s.remediation_step}`,
        OPEN: s.OPEN ?? 0,
        CLOSED: s.CLOSED ?? 0,
    }));

    return (
        <div className="analysis-pie-card type-step-card">
            <h4 className="type-step-card-title">{typeName}</h4>
            <div className="type-step-card-content">
                {/* ── left: donut ─────────────────────────────────── */}
                <div className="type-step-donut">
                    <PieChart width={140} height={140}>
                        <Pie
                            data={donutData}
                            cx="50%"
                            cy="50%"
                            innerRadius={42}
                            outerRadius={62}
                            startAngle={90}
                            endAngle={-270}
                            paddingAngle={donutData[1].value > 0 ? 3 : 0}
                            dataKey="value"
                            strokeWidth={0}
                        >
                            <Cell fill={PURPLE} />
                            <Cell fill={DIM} />
                            <Label
                                value={`${pct.toFixed(1)}%`}
                                position="center"
                                fill="#EDEBFF"
                                fontSize={15}
                                fontWeight={700}
                            />
                        </Pie>
                        <Tooltip content={<DonutTooltip />} />
                    </PieChart>
                </div>

                {/* ── right: meta + step bars ─────────────────────── */}
                <div className="type-step-body">
                    <div className="type-step-meta">
                        {formatInt(typeCount)} cases &middot; {pct.toFixed(1)}% of all
                    </div>

                    {stepRows.length > 0 && (
                        <div className="type-step-bars">
                            <ResponsiveContainer width="100%" height={110}>
                                <BarChart
                                    data={stepRows}
                                    margin={{ top: 4, right: 8, left: 0, bottom: 0 }}
                                    barCategoryGap="30%"
                                >
                                    <CartesianGrid
                                        vertical={false}
                                        strokeDasharray="3 3"
                                        stroke="rgba(255,255,255,0.06)"
                                    />
                                    <XAxis
                                        dataKey="step"
                                        tick={TICK}
                                        axisLine={false}
                                        tickLine={false}
                                    />
                                    <YAxis
                                        tick={TICK}
                                        axisLine={false}
                                        tickLine={false}
                                        width={40}
                                        tickFormatter={(v) => formatInt(v)}
                                    />
                                    <Tooltip
                                        cursor={{ fill: "rgba(111,58,255,0.10)" }}
                                        content={<StepTooltip />}
                                    />
                                    <Bar dataKey="OPEN" name="Open" stackId="a" fill={COLOR_OPEN} radius={[0, 0, 0, 0]} />
                                    <Bar dataKey="CLOSED" name="Closed" stackId="a" fill={COLOR_CLOSED} radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}


function RemediationTypeGrid({ loading, error, data }) {
    if (loading) {
        return (
            <div className="remediation-dashboard-loading">
                <span className="remediation-dashboard-loading-spinner" />
                <span>Loading data...</span>
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="remediation-dashboard-empty-state">{error ?? "No remediation type data"}</div>
        );
    }

    const items = data.data ?? [];
    const grandTotal = data.metadata?.grand_total ?? 0;

    if (items.length === 0) return null;

    return (
        <div className="remediation-dashboard-section">
            <div className="type-step-grid">
                {items.map((item) => (
                    <RemediationTypeCard
                        key={item.remediation_type}
                        remediation_type={item.remediation_type}
                        total_cases={item.total_cases}
                        percentage_of_all_cases={item.percentage_of_all_cases}
                        steps={item.steps}
                        grandTotal={grandTotal}
                    />
                ))}
            </div>
        </div>
    );
}


export default function CaseTypesByStatus() {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState();
    const [data, setData] = useState([]);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                setLoading(true);
                setError(null);
                const response = await fetchWithAuth(`${API_BASE}/ainstein/in/dashboard?chart_key=case_types_by_status`);
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const statsData = await response.json();
                setData(statsData ?? {});
                setLoading(false);
            } catch (err) {
                setError(err.message);
                setLoading(false);
            }
        };

        fetchStats();
    }, []);

    return (
        <section className="remediation-dashboard-section">
            <article className="remediation-dashboard-card">
                <h3 className="remediation-dashboard-card-title">Case Types by Status</h3>
                <RemediationTypeGrid loading={loading} error={error} data={data} />
            </article>
        </section>
    );
}